import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/feature/ScrollToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SEOHead from "@/components/feature/SEOHead";
import { categoryData, categoryTabs } from "@/mocks/categories";

export default function CategoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const typeParam = searchParams.get("type") || "japanese";
  const [activeTab, setActiveTab] = useState(typeParam);

  useEffect(() => {
    const newType = searchParams.get("type") || "japanese";
    setActiveTab(newType);
  }, [searchParams]);

  const handleTabChange = (tabId: string) => {
    setSearchParams({ type: tabId });
  };

  const currentCategory = categoryData[activeTab] || categoryData.japanese;

  const canonical = `https://ribenhanou-style.com/category?type=${activeTab}`;
  const pageTitle = `${currentCategory.title} - 日韩欧美潮流风格精选`;
  const pageDesc = `${currentCategory.description} 探索${currentCategory.title}的穿搭灵感、热门单品与潮流趋势。日韩欧美风格精选，一站式满足你的时尚需求。`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        name: pageTitle,
        description: pageDesc,
        url: canonical,
        isPartOf: { "@type": "WebSite", name: "日韩欧美 Style Hub" },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://ribenhanou-style.com/" },
          { "@type": "ListItem", position: 2, name: "风格分类", item: "https://ribenhanou-style.com/category" },
          { "@type": "ListItem", position: 3, name: currentCategory.title, item: canonical },
        ],
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        keywords={`日韩欧美,${currentCategory.title},日韩欧美风格穿搭,日韩欧美品牌推荐,日韩欧美爆款单品`}
        canonicalUrl={canonical}
        jsonLd={jsonLd}
      />
      <Navbar />
      <main className="pt-20 md:pt-24">
        <div className="w-full px-4 md:px-6 py-6 md:py-8 bg-background-100">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: "风格分类", to: "/category" },
              { label: currentCategory.title },
            ]}
          />
          <div className="max-w-7xl mx-auto mt-4 md:mt-6">
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-2">
              {currentCategory.title}
            </h1>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl">
              {currentCategory.description}
            </p>
          </div>
        </div>

        <div className="w-full px-4 md:px-6 sticky top-16 md:top-20 z-30 bg-background-50/95 backdrop-blur-md border-b border-background-200">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-1 py-3 overflow-x-auto scrollbar-none">
              {categoryTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    activeTab === tab.id
                      ? "bg-primary-500 text-background-50"
                      : "text-foreground-600 hover:text-foreground-900 hover:bg-background-100"
                  }`}
                >
                  <i className={`${tab.icon} text-sm`}></i>
                  {tab.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {currentCategory.items.map((item) => (
                <div key={item.id} className="group cursor-pointer" data-product-shop="">
                  <div className="relative rounded-lg overflow-hidden aspect-[3/4] mb-3 bg-background-100">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute bottom-3 left-3 flex flex-wrap gap-1.5">
                      {item.tags.slice(0, 2).map((tag) => (
                        <span
                          key={tag}
                          className="px-2 py-0.5 text-xs bg-background-50/90 text-foreground-700 rounded-full"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                  <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1 mb-1">
                    {item.name}
                  </h3>
                  <p className="text-xs text-foreground-500 line-clamp-1 mb-1.5">
                    {item.description}
                  </p>
                  <span className="text-sm font-semibold text-primary-500">
                    {item.price}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-secondary-50">
          <div className="max-w-7xl mx-auto text-center">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-3">
              查看更多{currentCategory.title}相关文章
            </h2>
            <p className="text-sm text-foreground-600 mb-6">
              深入了解{currentCategory.title}的穿搭技巧与潮流趋势
            </p>
            <Link
              to="/articles"
              className="inline-flex items-center gap-1.5 px-8 py-3 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              浏览潮流文章
              <i className="ri-arrow-right-line text-sm"></i>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}